import { Fragment, useState } from "react";
import { trainProgress } from "../../core/progress";
import type { AlertDigest, CommutePlan, CommuteSnapshot, NormalizedAlert } from "../../shared/types";
import { clock, healthTone, headsignLabel, minutes, planTag, previewTag, signedMinutes, slackLabel, stationLabel, type CardMode } from "../../core/format";
import { useSheetDismiss } from "../useSheetDismiss";
import { ScrollTopButton } from "./ScrollTopButton";
import { Clock } from "./Clock";

export function SupportingMetrics({ plan, snapshot }: { plan: CommutePlan | null; snapshot: CommuteSnapshot }) {
	const [expanded, setExpanded] = useState(false);
	const hasTravelLeg = snapshot.profile.hasAccessLeg;
	// With no leg to get there, there is no sum to show: the ride is the whole trip, and the card
	// already says how long it takes and when it lands.
	if (!plan || !hasTravelLeg) {
		return null;
	}

	const items: MetricProps[] = [
		{ label: "Out the door", value: minutes(plan.prepSeconds), unit: "min" },
		{
			label: "Drive",
			value: minutes(plan.accessDurationSeconds),
			unit: "min",
			note: plan.accessSource === "realtime" ? "traffic" : plan.accessSource,
		},
		{ label: "Station buffer", value: minutes(plan.stationBufferSeconds), unit: "min" },
		{ label: "Train ride", value: minutes(plan.rideSeconds), unit: "min" },
		...(plan.egressSeconds > 0
			? [{ label: `To ${snapshot.profile.finalLabel ?? "destination"}`, value: minutes(plan.egressSeconds), unit: "min", note: "shuttle" }]
			: []),
	];

	// The breakdown explains the answer rather than being it, so it stays shut until asked for.
	return (
		<section className="metrics" aria-label="Trip breakdown">
			<button type="button" className="metrics__toggle" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
				{expanded ? "Hide the breakdown" : "How this adds up"}
			</button>
			{expanded ? (
				<div className="metrics__grid">
					{items.map((item) => (
						<Metric key={item.label} {...item} />
					))}
				</div>
			) : null}
		</section>
	);
}

interface MetricProps {
	label: string;
	value: string;
	unit?: string;
	note?: string;
}

function Metric({ label, value, unit, note }: MetricProps) {
	return (
		<div className="metric">
			<span className="metric__label">{label}</span>
			<span className="metric__value">
				{value}
				{unit ? <span className="metric__unit">{unit}</span> : null}
			</span>
			{note ? <span className="metric__note">{note}</span> : null}
		</div>
	);
}

export function ServiceHealthCard({ snapshot, onShowNotices }: { snapshot: CommuteSnapshot; onShowNotices: () => void }) {
	const { health } = snapshot;
	const relevantAlerts = snapshot.alerts.filter((alert) => alert.affectsThisTrip);
	const hiddenAlerts = snapshot.alerts.filter((alert) => alert.suppressed);

	return (
		<section className={`panel panel--${healthTone(health.level)}`} aria-label="Service health">
			<header className="panel__head">
				<h2>{health.headline}</h2>
				<span className={`pill pill--${healthTone(health.level)}`}>{health.level.toUpperCase()}</span>
			</header>

			{health.reasons.length > 0 ? (
				<ul className="panel__reasons">
					{health.reasons.map((reason) => (
						<li key={reason}>{reason}</li>
					))}
				</ul>
			) : (
				<p className="panel__detail">{health.detail}</p>
			)}

			{health.headway.scheduledSeconds != null ? (
				<div className="headway">
					<span>
						Scheduled headway <strong>{minutes(health.headway.scheduledSeconds)} min</strong>
					</span>
					<span>
						Largest actual gap <strong>{minutes(health.headway.observedSeconds)} min</strong>
					</span>
				</div>
			) : null}

			{relevantAlerts.length > 0 ? (
				<AlertNotices
					alerts={relevantAlerts}
					hiddenCount={hiddenAlerts.length}
					digest={snapshot.alertDigest}
					onOpen={onShowNotices}
				/>
			) : hiddenAlerts.length > 0 ? (
				// The panel above has already said nothing affects the trip; this only offers the reading.
				<button type="button" className="alert alert--digest" onClick={onShowNotices}>
					<span className="alert__more">Show hidden alerts ({hiddenAlerts.length})</span>
				</button>
			) : null}
		</section>
	);
}

/**
 * BART's wording is written for the record, not for a phone. The digest is what gets read; the
 * notices themselves open in a sheet, and are shown outright whenever no digest exists, because
 * failing to summarise something is no reason to stop showing it.
 */
function AlertNotices({
	alerts,
	hiddenCount,
	digest,
	onOpen,
}: {
	alerts: NormalizedAlert[];
	hiddenCount: number;
	digest: AlertDigest | null;
	onOpen: () => void;
}) {
	const summarised = digest != null && digest.alertCount === alerts.length;

	if (!summarised) {
		return (
			<>
				{alerts.map((alert) => (
					<article key={alert.id} className="alert">
						<h3>{alert.header || alert.effect}</h3>
						{alert.description ? <p>{alert.description}</p> : null}
					</article>
				))}
				{hiddenCount > 0 ? (
					<button type="button" className="alert alert--digest" onClick={onOpen}>
						<span className="alert__more">Show hidden alerts ({hiddenCount})</span>
					</button>
				) : null}
			</>
		);
	}

	return (
		<button type="button" className="alert alert--digest" onClick={onOpen}>
			<span className="alert__summary">{digest.summary}</span>
			<span className="alert__more">Show alert details ({alerts.length + hiddenCount})</span>
		</button>
	);
}

/** Rendered at the app root: inside the zoomed detail column, `position: fixed` is not the viewport. */
export function AlertSheet({ alerts, onClose }: { alerts: NormalizedAlert[]; onClose: () => void }) {
	const dismiss = useSheetDismiss(onClose);
	const shown = alerts.filter((alert) => !alert.suppressed);
	const hidden = alerts.filter((alert) => alert.suppressed);

	return (
		<div className="sheet" role="dialog" aria-label="Service notices">
			<div className="sheet__scrim" onClick={onClose} />
			<div className="sheet__body" ref={dismiss.bodyRef} style={dismiss.style} {...dismiss.handlers}>
				<span className="sheet__grip" aria-hidden="true" />
				<ScrollTopButton targetRef={dismiss.bodyRef} className="sheet__toTop" />
				<header className="sheet__head">
					<h2>What BART said</h2>
					<button type="button" onClick={onClose} aria-label="Close">
						✕
					</button>
				</header>
				<div className="sheet__notices">
					{shown.map((alert) => (
						<article key={alert.id} className="alert">
							<h3>{alert.header || alert.effect}</h3>
							{alert.description ? <p>{alert.description}</p> : null}
						</article>
					))}
					{hidden.length > 0 ? (
						<>
							{/* A heading only earns its place when there is something above it to divide from. */}
							{shown.length > 0 ? <h3 className="sheet__noticesDivider">Not affecting this trip</h3> : null}
							{hidden.map((alert) => (
								<article key={alert.id} className="alert alert--muted">
									<h3>{alert.header || alert.effect}</h3>
									{alert.description ? <p>{alert.description}</p> : null}
								</article>
							))}
						</>
					) : null}
				</div>
			</div>
		</div>
	);
}

export function TrainBoard({
	snapshot,
	selectedId,
	mode,
	nowMs,
	destinationIndex = 0,
	showBoardingTime = false,
	onSelect,
	moreAvailable = false,
	onLoadMore,
	loadingMore = false,
}: {
	snapshot: CommuteSnapshot;
	selectedId: string | null;
	mode: CardMode;
	nowMs: number;
	destinationIndex?: number;
	showBoardingTime?: boolean;
	onSelect: (id: string) => void;
	/** Whether one more "show more trains" tap could still turn up something new today. */
	moreAvailable?: boolean;
	onLoadMore?: () => void;
	/** True while a tap's wider request is still in flight, so a second tap cannot double up. */
	loadingMore?: boolean;
}) {
	const boardingName = stationLabel(snapshot.profile.boardingStation.name);
	const preview = snapshot.targetPreview;
	const targetMs = snapshot.declaredTargetMs;
	const onboard = mode === "onboard";
	// Aboard, the station he left is behind him; the board is about where these trains are going.
	// Carried down as a stop id rather than the index, because a train that skips it has a shorter
	// list and the same index would silently name a different station.
	const alighting = snapshot.profile.destinations[destinationIndex] ?? snapshot.profile.destinations[0] ?? null;
	const alightingName = stationLabel(alighting?.name ?? snapshot.profile.boardingStation.name);

	return (
		<section className="board" aria-label="Upcoming trains">
			<header className="board__head">
				<h2>{onboard ? `Trains to ${alightingName}` : `Trains from ${boardingName}`}</h2>
				<span className="board__hint">{preview.length > 0 ? "Running now" : "Tap a train"}</span>
			</header>

			<ol className="board__list">
				{snapshot.options.map((plan) => (
					<Row
						key={plan.id}
						plan={plan}
						snapshot={snapshot}
						selectedId={selectedId}
						mode={mode}
						nowMs={nowMs}
						destinationStopId={alighting?.id ?? null}
						showBoardingTime={showBoardingTime}
						onSelect={onSelect}
					/>
				))}
			</ol>

			{moreAvailable ? (
				<button type="button" className="board__more" onClick={onLoadMore} disabled={loadingMore} aria-busy={loadingMore}>
					{loadingMore ? "Looking further out…" : "Show more trains — another hour, or five more"}
				</button>
			) : null}

			{preview.length > 0 ? (
				<>
					<header className="board__head board__head--divided">
						<h2>Around your {targetMs == null ? "target" : clock(targetMs, snapshot.timezone)} arrival</h2>
						<span className="board__hint">
							{preview[0] ? new Date(preview[0].boarding.predictedMs).toLocaleDateString("en-US", { weekday: "long", timeZone: snapshot.timezone }) : ""} · scheduled
						</span>
					</header>

					<ol className="board__list board__list--preview">
						{preview.map((plan) => (
							<Row key={plan.id} plan={plan} snapshot={snapshot} selectedId={selectedId} mode={mode} nowMs={nowMs} onSelect={onSelect} preview />
						))}
					</ol>
				</>
			) : null}
		</section>
	);
}

interface RowProps {
	plan: CommutePlan;
	snapshot: CommuteSnapshot;
	selectedId: string | null;
	mode: CardMode;
	nowMs: number;
	/** Where he is getting off. Only consulted aboard, where it is the whole question. */
	destinationStopId?: string | null;
	showBoardingTime?: boolean;
	onSelect: (id: string) => void;
	/** Preview rows describe another day, so "how long until you must move" is meaningless. */
	preview?: boolean;
}

function Row({ plan, snapshot, selectedId, mode, nowMs, destinationStopId = null, showBoardingTime = false, onSelect, preview = false }: RowProps) {
	const tag = preview ? previewTag(plan, snapshot.previewRecommendedId) : planTag(plan, snapshot, mode, nowMs);
	const finalLabel = snapshot.profile.finalLabel;
	const onboard = mode === "onboard";
	// Null when this train does not stop where he is going, which is the one thing that makes a
	// train useless once he is already moving.
	const alighting = destinationStopId == null ? plan.destinations[0] : plan.destinations.find((stop) => stop.stopId === destinationStopId);

	// Being at school is the deadline, so that arrival leads and the platform it comes from
	// drops to the quiet line underneath.
	const arrivesAtSchool = finalLabel != null && plan.finalArrivalMs != null;
	// Aboard, the only time worth printing is when this train reaches his stop, and that is
	// already the big number on the left. Anything else here is a second arrival to misread.
	// Planning a pickup is the exception: the departure is how this train is named everywhere else.
	const primary = onboard
		? showBoardingTime
			? { label: stationLabel(plan.boarding.stopName), ms: plan.boarding.predictedMs }
			: null
		: arrivesAtSchool
			? { label: finalLabel, ms: plan.finalArrivalMs as number }
			: alighting
				? { label: stationLabel(alighting.stopName), ms: alighting.predictedMs }
				: null;
	// The other stops this train also reaches, now that there can be more than one — folded into
	// the same quiet line rather than the big number, which only has room for the one he picked.
	const others = onboard
		? []
		: arrivesAtSchool
			? plan.destinations
			: plan.destinations.filter((stop) => stop.stopId !== alighting?.stopId);
	// Aboard, every train on the board has already left, so greying them all out says nothing
	// except that the whole list is unusable — which is the opposite of what it is for. What does
	// earn the grey is a train that never reaches the stop he picked.
	const unreachable = preview ? false : onboard ? alighting == null : !plan.feasible;
	// Aboard, the platform it left from is behind him; when it reaches his stop is the question.
	const leadMs = onboard ? (alighting?.predictedMs ?? null) : plan.boarding.predictedMs;

	const timing = onboard ? alighting : plan.boarding;
	const delay = timing?.delaySeconds != null && Math.abs(timing.delaySeconds) >= 60 ? ` ${signedMinutes(timing.delaySeconds)}` : "";
	const meta = [
		others.length > 0 ? others.map((stop) => `${stationLabel(stop.stopName)} ${clock(stop.predictedMs, snapshot.timezone)}`).join(" · ") : null,
		timing ? `${timing.source === "realtime" ? "live" : "scheduled"}${delay}` : null,
		headsignLabel(plan.headsign),
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<li>
			<button
				type="button"
				className={`row ${plan.id === selectedId ? "is-selected" : ""} ${unreachable ? "is-unreachable" : ""}`}
				onClick={() => onSelect(plan.id)}
			>
				<span className="row__line" style={{ background: plan.routeColor }} aria-hidden="true" />

				<Clock ms={leadMs} timezone={snapshot.timezone} className="row__depart" />
				<span className={`pill pill--${tag.tone} row__tag`}>{tag.text}</span>

				<span className="row__final">
					{primary ? (
						<>
							<em>{primary.label}</em>
							<Clock ms={primary.ms} timezone={snapshot.timezone} className="row__finalTime" />
						</>
					) : null}
				</span>

				<span className="row__wait">
					{preview ? (
						plan.leaveByMs == null ? null : (
							<>
								leave <Clock ms={plan.leaveByMs} timezone={snapshot.timezone} />
							</>
						)
					) : onboard ? (
						// "left" is true of every train on this board and answers a question he stopped
						// asking when the doors shut. How long until his stop is the live one.
						alighting == null ? (
							"no stop"
						) : (
							`${Math.max(0, Math.round((alighting.predictedMs - nowMs) / 60_000))} min`
						)
					) : (
						slackLabel(plan)
					)}
				</span>

				<span className="row__meta">{meta}</span>
			</button>
		</li>
	);
}

/** Never shrink to a line or two as the destination nears: a short list reads as a broken one. */
const RIDE_SCHEDULE_MINIMUM = 7;

/**
 * The stops worth showing, and where the train is among them.
 *
 * Before boarding the interesting part is the approach, because a delay already baked into it is
 * about to become his. Once aboard it is the run still ahead, ending where he gets off.
 */
export function rideSchedule(plan: CommutePlan, onboard: boolean, nowMs: number, destinationStopId: string | null): CommutePlan["upstream"] {
	const whole = [...plan.upstream, plan.boarding, ...plan.downstream];
	if (!onboard) {
		return [...plan.upstream, plan.boarding];
	}
	const alightAt = destinationStopId ?? plan.destinations[0]?.stopId ?? null;
	const endIndex = alightAt ? whole.findIndex((stop) => stop.stopId === alightAt) : -1;
	const ride = endIndex >= 0 ? whole.slice(0, endIndex + 1) : whole;
	// Count back from where he gets off, so the list keeps its length by showing more of the
	// track already behind him rather than inventing stops beyond the end of the journey.
	const ahead = ride.filter((stop) => stop.predictedMs > nowMs).length;
	return ride.slice(Math.max(0, ride.length - Math.max(ahead + 1, RIDE_SCHEDULE_MINIMUM)));
}

export function UpstreamTrace({
	plan,
	snapshot,
	nowMs,
	onboard = false,
	destinationStopId = null,
}: {
	plan: CommutePlan | null;
	snapshot: CommuteSnapshot;
	nowMs: number;
	onboard?: boolean;
	destinationStopId?: string | null;
}) {
	if (!plan) {
		return null;
	}
	const stops = rideSchedule(plan, onboard, nowMs, destinationStopId);
	if (stops.length === 0) {
		return null;
	}

	const anyLive = stops.some((stop) => stop.source === "realtime");
	const progress = trainProgress(plan, nowMs);

	return (
		<section className="trace" aria-label={onboard ? "Current ride schedule" : "Train progress before boarding"}>
			<header className="board__head">
				<h2>{onboard ? "Current ride schedule" : "Before it reaches you"}</h2>
				<span className="board__hint">{anyLive ? "Live where marked" : "Scheduled only — no live data for this train"}</span>
			</header>

			<ol className="trace__list">
				{stops.map((stop, index) => {
					const atThisStop = progress?.fromStopId === stop.stopId && progress.toStopId === stop.stopId;
					const approachingThis = progress?.toStopId === stop.stopId && progress.fromStopId !== stop.stopId;
					const isBoarding = onboard ? index === stops.length - 1 : index === stops.length - 1;
					return (
						<Fragment key={stop.stopId}>
							{approachingThis ? (
								<li className="trace__between" aria-label="Train is between stations">
									<span className="trace__betweenDot" />
									<span>train here</span>
								</li>
							) : null}
							<li className={`${stop.passed ? "is-passed" : ""} ${isBoarding ? "is-boarding" : ""} ${atThisStop ? "is-here" : ""}`}>
								<span className="trace__stop">{stationLabel(stop.stopName)}</span>
								<Clock ms={stop.predictedMs} timezone={snapshot.timezone} className="trace__time" />
								<span className={`trace__flag ${stop.source === "realtime" && !stop.passed ? "is-live" : ""}`}>{traceFlag(stop)}</span>
							</li>
						</Fragment>
					);
				})}
			</ol>
		</section>
	);
}

/** What we actually know about this stop, in the fewest words. */
function traceFlag(stop: CommutePlan["boarding"]): string {
	const delay = stop.delaySeconds;
	const lateness = delay == null || Math.abs(delay) < 60 ? "" : delay > 0 ? ` +${Math.round(delay / 60)}` : ` ${Math.round(delay / 60)}`;

	if (stop.passed) {
		return `departed${lateness}`;
	}
	if (stop.source !== "realtime") {
		return "scheduled";
	}
	if (!lateness) {
		return "on time";
	}
	return `${lateness.trim()} min ${delay! > 0 ? "late" : "early"}`;
}
